/*
 * This file is part of the https://gitlab-intern.easd-support.de/easd/p26s26_energie_watchdog project.
 *
 * (c) 2026 EA Systems Dresden GmbH
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

/* Import types */
import type {
    DeviceSensorConfig,
    DeviceSensorCsvMetadataGroup,
    DeviceSensorGroup,
    DeviceSensorSystemGroup,
    DeviceSensorTranslationEntry,
    DeviceSensorTranslationTable,
    ResolvedDeviceNames,
} from '@/types/deviceSensor';
import type { SensorMetadataEntry } from '@/lib/sensors/csv/csvToSensorMetadata';

const EMPTY_DEVICE_SENSOR_CONFIG: DeviceSensorConfig = {
    baseSensorTypes: [],
    deviceMetadata: {},
    sensorMetadata: {},
    sensorGroups: ['DTC', 'BL', 'HE', 'PV', 'WL'],
    sensorTypes: {
        DTC: [],
        BL: [],
        HE: [],
        PV: [],
        WL: [],
    },
    translationTable: [],
};

const SENSOR_GROUP_METADATA: Record<
    Exclude<DeviceSensorSystemGroup, 'DTC'>,
    {
        anomalies: string[];
        metadataGroup: DeviceSensorCsvMetadataGroup;
    }
> = {
    BL: { anomalies: ['bl_night', 'bl'], metadataGroup: 'BL' },
    HE: { anomalies: ['heating_energy'], metadataGroup: 'HE' },
    PV: { anomalies: ['pv'], metadataGroup: 'PV' },
    WL: { anomalies: ['water_bl'], metadataGroup: 'WATER' },
};

function findTranslationEntry(
    deviceName: string,
    translationTable: DeviceSensorTranslationTable,
): DeviceSensorTranslationEntry | null {
    return translationTable.find((entry) => {
        return entry.virtual === deviceName || entry.real === deviceName;
    }) ?? null;
}

export class DeviceSensorCatalog {
    private readonly config: DeviceSensorConfig;

    constructor(config: DeviceSensorConfig = EMPTY_DEVICE_SENSOR_CONFIG) {
        this.config = config;
    }

    private getConfiguredDeviceNames(): string[] {
        if (this.config.translationTable.length > 0) {
            return this.config.translationTable.map((entry) => entry.virtual);
        }

        return Object.keys(this.config.deviceMetadata);
    }

    getRealDevices(group: DeviceSensorGroup = null): string[] {
        if (!group) {
            if (this.config.translationTable.length > 0) {
                return this.config.translationTable.map((entry) => entry.real);
            }

            return this.getConfiguredDeviceNames();
        }

        return this.getConfiguredDeviceNames()
            .filter((deviceName) => this.getSensorGroups(deviceName).includes(group))
            .map((deviceName) => this.resolveDeviceNames(deviceName).realDeviceName);
    }

    getVirtualDevices(group: DeviceSensorGroup = null): string[] {
        if (!group) {
            return this.getConfiguredDeviceNames();
        }

        return this.getConfiguredDeviceNames()
            .filter((deviceName) => this.getSensorGroups(deviceName).includes(group))
            .map((deviceName) => this.resolveDeviceNames(deviceName).virtualDeviceName);
    }

    getVirtualDevicesByMetadataGroups(
        groups: readonly DeviceSensorCsvMetadataGroup[],
    ): string[] {
        return this.getConfiguredDeviceNames()
            .filter((deviceName) => {
                const { virtualDeviceName } = this.resolveDeviceNames(deviceName);
                const metadataEntry = this.config.deviceMetadata[virtualDeviceName];

                return groups.some((group) => Boolean(metadataEntry?.anomaly[group]));
            })
            .map((deviceName) => this.resolveDeviceNames(deviceName).virtualDeviceName);
    }

    isGroupEnabled(deviceName: string, group: Exclude<DeviceSensorSystemGroup, 'DTC'>): boolean {
        return this.isMetadataGroupEnabled(deviceName, this.getMetadataGroup(group));
    }

    getMetadataGroup(group: Exclude<DeviceSensorSystemGroup, 'DTC'>): DeviceSensorCsvMetadataGroup {
        return SENSOR_GROUP_METADATA[group].metadataGroup;
    }

    isMetadataGroupEnabled(deviceName: string, group: DeviceSensorCsvMetadataGroup): boolean {
        const { virtualDeviceName } = this.resolveDeviceNames(deviceName);

        return this.config.deviceMetadata[virtualDeviceName]?.enabled[group] === true;
    }

    getDeviceDescription(deviceName: string): string | null {
        const { virtualDeviceName } = this.resolveDeviceNames(deviceName);

        return this.config.deviceMetadata[virtualDeviceName]?.description ?? null;
    }

    getDeviceSystem(deviceName: string): string | null {
        const { virtualDeviceName } = this.resolveDeviceNames(deviceName);

        return this.config.deviceMetadata[virtualDeviceName]?.system ?? null;
    }

    getDeviceMetadataGroups(deviceName: string): DeviceSensorCsvMetadataGroup[] {
        const { virtualDeviceName } = this.resolveDeviceNames(deviceName);

        return Object.keys(
            this.config.deviceMetadata[virtualDeviceName]?.anomaly ?? {},
        ) as DeviceSensorCsvMetadataGroup[];
    }

    getDevicesBySystem(system: string): string[] {
        return this.getConfiguredDeviceNames()
            .filter((deviceName) => this.getDeviceSystem(deviceName) === system)
            .map((deviceName) => this.resolveDeviceNames(deviceName).virtualDeviceName);
    }

    getSensorMetadata(
        deviceName: string,
        group: Exclude<DeviceSensorSystemGroup, 'DTC'> | null = null,
    ): SensorMetadataEntry | null {
        const { virtualDeviceName } = this.resolveDeviceNames(deviceName);
        const metadataEntries = this.config.sensorMetadata[virtualDeviceName];

        if (!metadataEntries?.length) {
            return null;
        }

        if (!group) {
            return metadataEntries[0] ?? null;
        }

        const preferredAnomalies = SENSOR_GROUP_METADATA[group].anomalies;

        for (const preferredAnomaly of preferredAnomalies) {
            const matchingEntry = metadataEntries.find((entry) => {
                return entry.anomaly === preferredAnomaly;
            });

            if (matchingEntry) {
                return matchingEntry;
            }
        }

        return metadataEntries[0] ?? null;
    }

    getSensorGroups(deviceName: string | null = null): DeviceSensorSystemGroup[] {
        if (!deviceName) {
            return this.config.sensorGroups.filter((group) => {
                if (group === 'DTC') {
                    return true;
                }

                return this.getVirtualDevices(group).length > 0;
            });
        }

        const { virtualDeviceName } = this.resolveDeviceNames(deviceName);
        const metadataEntry = this.config.deviceMetadata[virtualDeviceName];

        return this.config.sensorGroups.filter((group) => {
            if (group === 'DTC') {
                return true;
            }

            return Boolean(metadataEntry?.anomaly[this.getMetadataGroup(group)]);
        });
    }

    getSensorTypes(group: DeviceSensorGroup | string): string[] {
        if (group === null) {
            return [...this.config.baseSensorTypes];
        }

        if (!(group in this.config.sensorTypes)) {
            return [];
        }

        return [...this.config.sensorTypes[group as keyof typeof this.config.sensorTypes]];
    }

    resolveDeviceNames(deviceName: string): ResolvedDeviceNames {
        const translationEntry = findTranslationEntry(deviceName, this.config.translationTable);

        return {
            realDeviceName: translationEntry?.real ?? deviceName,
            virtualDeviceName: translationEntry?.virtual ?? deviceName,
        };
    }
}
